import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@app/app.config';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GoogleAuthService {
  private oauthService = inject(OAuthService);
  private tokenReadySubject = new ReplaySubject<string>(1);
  public tokenReady$ = this.tokenReadySubject.asObservable();

  private userEmailSubject = new BehaviorSubject<string | null>(null);
  private notRegisteredEmailSubject = new BehaviorSubject<boolean>(false);
  private invalidMethodSubject = new BehaviorSubject<boolean>(false);
  private userRegisteredSubject = new BehaviorSubject<boolean>(false);
  private userPendingAprobationSubject = new BehaviorSubject<boolean>(false);
  
  public userEmail$ = this.userEmailSubject.asObservable();
  public notRegisteredEmail$ = this.notRegisteredEmailSubject.asObservable();
  public invalidMethod$ = this.invalidMethodSubject.asObservable();
  public userRegistered$ = this.userRegisteredSubject.asObservable();
  public userPending$ = this.userPendingAprobationSubject.asObservable();

  constructor() {
    this.configureOAuth();
    this.tryLoginSilently();
  }

  private configureOAuth() {
    const config: AuthConfig = {
      issuer: 'https://accounts.google.com',
      strictDiscoveryDocumentValidation: false,
      clientId: AppConfig.googleClientId,
      redirectUri: AppConfig.redirectUriGoogle,
      scope: AppConfig.scopes
    };
    this.oauthService.configure(config);
    this.oauthService.setupAutomaticSilentRefresh();
  }

  public tryLoginSilently() {
    void this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      if (this.oauthService.hasValidAccessToken()) {
        const token = this.oauthService.getIdToken();
        this.tokenReadySubject.next(token);
      }
    });
  }

  private setAuthIntent(intent: 'login' | 'register') {
    document.cookie = `auth_intent=${intent}; path=/;`;
  }

  getAuthIntent(): 'login' | 'register' | null {
    const match = document.cookie.match(/(?:^|; )auth_intent=([^;]+)/);
    return match ? (match[1] as 'login' | 'register') : null;
  }

  clearAuthIntent() {
    document.cookie = 'auth_intent=; Max-Age=0; path=/';
  }

  getToken(): string | null {
    return this.oauthService.getIdToken();
  }

  getProfile() {
    const claims = this.oauthService.getIdentityClaims();
    if (claims) {
      const email = claims['email'];
      this.userEmailSubject.next(email);
      return claims;
    }
    return null;
  }

  getStoredEmail(): string | null {
    return this.userEmailSubject.value;
  }

  setGoogleNotRegistered(value: boolean) {
    this.notRegisteredEmailSubject.next(value);
  }
 
  setInvalidMethod(value: boolean) {
    this.invalidMethodSubject.next(value);
  }
 
  setUserRegistered(value: boolean) {
    this.userRegisteredSubject.next(value);
  }

  setUserPendingAprobation(value: boolean) {
    this.userPendingAprobationSubject.next(value);
  }
 
  consumeGoogleNotRegistered(): boolean {
    const isNotRegistered = this.notRegisteredEmailSubject.value;
    this.notRegisteredEmailSubject.next(false);
    return isNotRegistered;
  }
 
  consumeInvalidMethod(): boolean {
    const isInvalidMethod = this.invalidMethodSubject.value;
    this.invalidMethodSubject.next(false);
    return isInvalidMethod;
  }
 
  consumeUserRegistered(): boolean {
    const isUserRegistered = this.userRegisteredSubject.value;
    this.userRegisteredSubject.next(false);
    return isUserRegistered;
  }

  consumeUserPendingAprobation(): boolean {
    const isUserPending = this.userPendingAprobationSubject.value;
    this.userPendingAprobationSubject.next(false);
    return isUserPending;
  }

  login() {
    this.setAuthIntent('login');
    this.oauthService.initLoginFlow(undefined, {
      customQueryParams: {
        prompt: 'select_account'
      }
    });
  }

  register() {
    this.setAuthIntent('register');
    this.oauthService.initLoginFlow(undefined, {
      customQueryParams: {
        prompt: 'consent select_account'
      }
    });
  }

  logout() {
    this.oauthService.logOut();
  }

  clear() {
    this.tokenReadySubject.next('');
    this.userEmailSubject.next(null);
    this.clearAuthIntent();
  }

}
